import React, { useState, useEffect } from "react";
import {
  Form,
  Button,
  Table,
  Card,
  Badge,
  Modal,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import {
  FaCheck,
  FaTimes,
  FaEdit,
  FaTrash,
  FaPlus,
  FaExclamationTriangle
} from "react-icons/fa";
import TagSnippetCard from "./TagSnippetCard";
import "../../ComponentsCss/ConsentManagementTab.css";
import { useAuth } from "../../contexts/AuthContext";

const ConsentManagementTab = ({ userData, showNotification,logToAccessLogs }) => {
  const [consents, setConsents] = useState([]);
  const [searchIdentity, setSearchIdentity] = useState("");
  const [searchedConsent, setSearchedConsent] = useState(null);
  const [searching, setSearching] = useState(false);
  const [submittedConsents, setSubmittedConsents] = useState([]);
  const [loadingSubmitted, setLoadingSubmitted] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailedConsent, setDetailedConsent] = useState(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState(null);
  const { profile } = useAuth();

  const [showEditSubmittedModal, setShowEditSubmittedModal] = useState(false);
  const [editingSubmittedConsent, setEditingSubmittedConsent] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    mobile: "",
    age: "",
    guardianName: "",
    guardianRelationship: "",
    guardianMobile: "",
    privacyPolicyConsent: false,
    thirdPartyConsent: false,
    emailMarketingConsent: false
  });

  const [consentStats, setConsentStats] = useState({
    total: 0,
    active: 0,
    withdrawn: 0,
    expired: 0
  });

  const [tagEvents, setTagEvents] = useState([]);

  const [consentForm, setConsentForm] = useState({
    purpose: "",
    dataCategory: "",
    processingType: "",
    retentionPeriod: "",
    validityPeriod: "",
    granularConsent: false,
    consentMethod: "",
    dataPrAPcipalinfo: "",
    isChildData: false,
    parentalConsent: {
      parentGuardianName: "",
      relationToChild: "",
      contactNumber: "",
      verificationConfirmed: false
    }
  });

  const getUserRegion = () => {
    return sessionStorage.getItem("userRegion") || "AP";
  };

  // âœ… FIX: fetchConsents â€” removed duplicate `const userId = organizationId` which caused ReferenceError
  const fetchConsents = async () => {
    const organizationId = profile?.orgId; // âœ… declare first
    const region = getUserRegion();

    try {
      const res = await fetch(
        "https://nv6f7v7hzf.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/consents/list",
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Region: region,
            organizationId: organizationId
          }
        }
      );
      const data = await res.json();
      setConsents(data.consents || []);
      setConsentStats(data.stats || consentStats);
    } catch (err) {
      console.error("Error fetching consents", err);
    }
  };

  // âœ… fetchSubmittedConsents â€” org-wide, cross-region
  const fetchSubmittedConsents = async () => {
    const organizationId = profile?.orgId;
    const region = getUserRegion();
    setLoadingSubmitted(true);
    try {
      const res = await fetch(
        `https://hi2mzuo9o4.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/consent/organization/${organizationId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Region: region
          }
        }
      );
      if (res.ok) {
        const data = await res.json();
        setSubmittedConsents(data || []);
      } else {
        showNotification("Failed to load submitted consents", "danger");
      }
    } catch (err) {
      console.error("Error fetching submitted consents", err);
      showNotification("Error loading submitted consents", "danger");
    } finally {
      setLoadingSubmitted(false);
    }
  };

  const fetchTagEvents = async () => {
    const tenantId = userData?.tenantId || userData?.cognitoUsername;
    const region = getUserRegion();
    try {
      const res = await fetch(
        `https://c0c9mbekzl.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/consents/tag-events?tenantId=${tenantId}&region=${region}`,
        { method: "GET" }
      );
      const data = await res.json();
      setTagEvents(data || []);
    } catch (err) {
      console.error("Error fetching tag events", err);
    }
  };

  // âœ… FIX: Single useEffect â€” removed duplicate first useEffect that ran before fetchSubmittedConsents was declared
  useEffect(() => {
    fetchConsents();
    fetchSubmittedConsents();
    fetchTagEvents();

    function onConsentChange(event) {
      fetchConsents();
      fetchSubmittedConsents();
      fetchTagEvents();
      showNotification(`Consent status changed: ${event.detail.status}`, "info");
    }

    window.addEventListener("susanConsentChange", onConsentChange);
    return () => window.removeEventListener("susanConsentChange", onConsentChange);
  }, [userData]);

  const viewConsentDetails = async (identity) => {
    const organizationId = profile?.orgId;
    const region = getUserRegion();

    try {
      const res = await fetch(
        `https://t331ip10m7.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/consent/details?identity=${identity}&organizationId=${organizationId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Region: region
          }
        }
      );

      if (res.ok) {
        const data = await res.json();
        setDetailedConsent(data);
        setShowDetailsModal(true);
      }
    } catch (err) {
      console.error("Error fetching details:", err);
      showNotification("Error fetching consent details", "danger");
    }
  };

  const handleEditSubmittedConsent = (consent) => {
    setEditingSubmittedConsent(consent);
    setEditForm({
      name: consent.name,
      email: consent.email,
      mobile: consent.mobile,
      age: consent.age,
      guardianName: consent.guardianName || "",
      guardianRelationship: consent.guardianRelationship || "",
      guardianMobile: consent.guardianMobile || "",
      privacyPolicyConsent: consent.consentPreferences?.privacyPolicy || false,
      thirdPartyConsent: consent.consentPreferences?.thirdParty || false,
      emailMarketingConsent: consent.consentPreferences?.emailMarketing || false
    });
    setShowEditSubmittedModal(true);
  };

  const handleSaveEditSubmittedConsent = async (e) => {
    e.preventDefault();

    const organizationId = profile?.orgId;
    const region = getUserRegion();

    const payload = {
      organizationId: organizationId,
      name: editForm.name,
      email: editForm.email,
      mobile: editForm.mobile,
      age: parseInt(editForm.age),
      guardianName: parseInt(editForm.age) < 18 ? editForm.guardianName : null,
      guardianRelationship: parseInt(editForm.age) < 18 ? editForm.guardianRelationship : null,
      guardianMobile: parseInt(editForm.age) < 18 ? editForm.guardianMobile : null,
      guardianDeclaration: parseInt(editForm.age) < 18 ? true : null,
      privacyPolicyConsent: editForm.privacyPolicyConsent,
      thirdPartyConsent: editForm.thirdPartyConsent,
      emailMarketingConsent: editForm.emailMarketingConsent,
      bannerConsent: true,
      finalConsent: true,
      timestamp: new Date().toISOString(),
      ipAddress: "ADMIN_UPDATE",
      region: region
    };

    try {
      const res = await fetch(
        `https://44eqa10l00.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/consent/update/${editingSubmittedConsent.consentId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Region: region
          },
          body: JSON.stringify(payload)
        }
      );

      if (res.ok) {
        showNotification("Consent updated successfully!", "success");
        logToAccessLogs?.({
  action:      'Submitted Consent Updated',
  severity:    'warning',
  description: `Updated user-submitted consent for ${editForm.email}`,});
        setShowEditSubmittedModal(false);
        setEditingSubmittedConsent(null);
        fetchSubmittedConsents();
      } else {
        const error = await res.json();
        showNotification(`Failed to update: ${error.message}`, "danger");
      }
    } catch (err) {
      console.error("Error updating consent:", err);
      showNotification("Error updating consent", "danger");
    }
  };

  const handleDeleteSubmittedConsent = async (consentId) => {
    if (!window.confirm("Are you sure you want to delete this consent record? This action cannot be undone.")) {
      return;
    }

    const organizationId = profile?.orgId;
    const region = getUserRegion();

    try {
      const res = await fetch(
        `https://b0doo1lf30.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/consent/delete/${consentId}?organizationId=${organizationId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Region: region
          }
        }
      );

      if (res.ok) {
        showNotification("Consent deleted successfully!", "success");
        logToAccessLogs?.({
  action:      'Submitted Consent Deleted',
  severity:    'critical',
  description: `User-submitted consent deleted â€” ID: ${consentId}`,});
        fetchSubmittedConsents();
      } else {
        const error = await res.json();
        showNotification(`Failed to delete: ${error.message}`, "danger");
      }
    } catch (err) {
      console.error("Error deleting consent:", err);
      showNotification("Error deleting consent", "danger");
    }
  };

  // âœ… FIX: handleSaveConsent â€” removed `const userId = organizationId` before declaration
  const handleSaveConsent = async (e) => {
    e.preventDefault();

    if (consentForm.isChildData) {
      if (
        !consentForm.parentalConsent.parentGuardianName ||
        !consentForm.parentalConsent.relationToChild ||
        !consentForm.parentalConsent.contactNumber
      ) {
        showNotification("Please provide all parental/guardian details", "danger");
        return;
      }
      if (!consentForm.parentalConsent.verificationConfirmed) {
        showNotification("Please confirm that verifiable parental consent has been obtained", "danger");
        return;
      }
    }

    const organizationId = profile?.orgId; // âœ… declare first
    const region = getUserRegion();

    const isEdit = !!selectedConsent;
    const url = isEdit
      ? `https://4unaatvcf7.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/consents/update/${selectedConsent.consentId}`
      : "https://0vcsj8lti7.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/consents/create";

    const method = isEdit ? "PUT" : "POST";

    const body = {
      organizationId: organizationId,
      purpose: consentForm.purpose,
      dataCategory: consentForm.dataCategory,
      processingType: consentForm.processingType,
      retentionPeriod: consentForm.retentionPeriod,
      validityPeriod: consentForm.validityPeriod,
      granularConsent: consentForm.granularConsent,
      consentMethod: consentForm.consentMethod,
      dataPrincipalInfo: consentForm.dataPrAPcipalinfo,
      isChildData: consentForm.isChildData,
      parentalConsent: consentForm.parentalConsent,
      region: getUserRegion()
    };

    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Region: region,
          organizationId: organizationId
        },
        body: JSON.stringify(body)
      });

      if (res.ok) {
        showNotification(
          isEdit ? "Consent updated successfully" : "Consent record created successfully",
          "success"
        );
        logToAccessLogs?.({
  action:      isEdit ? 'Consent Updated' : 'Consent Created',
  severity:    isEdit ? 'warning' : 'success',
  description: `${isEdit ? 'Updated' : 'Created'} consent record â€” Purpose: ${consentForm.purpose}, Category: ${consentForm.dataCategory}`,});
        setShowConsentModal(false);
        setSelectedConsent(null);
        fetchConsents();
        fetchSubmittedConsents(); // âœ… refresh both tables
        resetForm();
      } else {
        showNotification("Failed to save consent", "danger");
      }
    } catch (err) {
      console.error("Error saving consent", err);
      showNotification("Error saving consent", "danger");
    }
  };

  const handleWithdrawConsent = async (consentId) => {
    const organizationId = profile?.orgId;
    const region = getUserRegion();

    try {
      const res = await fetch(
        `https://00hfbvtn2b.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/consents/withdraw/${consentId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Region: region,
            organizationId: organizationId
          }
        }
      );

      if (res.ok) {
        showNotification("Consent withdrawn successfully", "success");
        logToAccessLogs?.({
  action:      'Consent Withdrawn',
  severity:    'warning',
  description: `Consent withdrawn â€” ID: ${consentId}`,});
        fetchConsents();
      }
    } catch (err) {
      console.error("Error withdrawing consent", err);
    }
  };

  const handleEditClick = (consent) => {
    setSelectedConsent(consent);
    setConsentForm({
      purpose: consent.purpose || "",
      dataCategory: consent.dataCategory || "",
      processingType: consent.processingType || "",
      retentionPeriod: consent.retentionPeriod || "",
      validityPeriod: consent.validityPeriod || "",
      granularConsent: consent.granularConsent || false,
      consentMethod: consent.consentMethod || "",
      dataPrAPcipalinfo: consent.dataPrAPcipalinfo || "",
      isChildData: consent.isChildData || false,
      parentalConsent:
        consent.isChildData && consent.parentalConsent
          ? {
              parentGuardianName: consent.parentalConsent.parentGuardianName || "",
              relationToChild: consent.parentalConsent.relationToChild || "",
              contactNumber: consent.parentalConsent.contactNumber || "",
              verificationConfirmed: consent.parentalConsent.verificationConfirmed || false
            }
          : {
              parentGuardianName: "",
              relationToChild: "",
              contactNumber: "",
              verificationConfirmed: false
            }
    });
    setShowConsentModal(true);
  };

  const handleDeleteConsent = async (consentId) => {
    const organizationId = profile?.orgId;
    const region = getUserRegion();

    if (!window.confirm("Are you sure you want to delete this consent?")) return;

    try {
      const res = await fetch(
        `https://anuirxr986.execute-api.ap-southeast-2.amazonaws.com/prod/api/dpdp/consents/delete/${consentId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Region: region,
            organizationId: organizationId
          }
        }
      );

      if (res.ok) {
        showNotification("Consent deleted successfully", "success");
        logToAccessLogs?.({
  action:      'Consent Deleted',
  severity:    'critical',
  description: `Consent record deleted â€” ID: ${consentId}`,});
        fetchConsents();
      } else {
        showNotification("Failed to delete consent", "danger");
      }
    } catch (err) {
      console.error("Error deleting consent", err);
      showNotification("Error deleting consent", "danger");
    }
  };

  const resetForm = () => {
    setConsentForm({
      purpose: "",
      dataCategory: "",
      processingType: "",
      retentionPeriod: "",
      validityPeriod: "",
      granularConsent: false,
      consentMethod: "",
      dataPrAPcipalinfo: "",
      isChildData: false,
      parentalConsent: {
        parentGuardianName: "",
        relationToChild: "",
        contactNumber: "",
        verificationConfirmed: false
      }
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "success",
      ACTIVE: "success",
      withdrawn: "warning",
      WITHDRAWN_ALL: "danger",
      PARTIALLY_WITHDRAWN: "warning",
      expired: "danger",
      pending: "info"
    };
    return <Badge bg={variants[status] || "secondary"}>{status}</Badge>;
  };

  return (
    <div className="consent-management-tab">
      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h3>{consentStats.total}</h3>
              <p>Total Consents</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-success">
            <Card.Body>
              <h3 className="text-success">{consentStats.active}</h3>
              <p>Active</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-warning">
            <Card.Body>
              <h3 className="text-warning">{consentStats.withdrawn}</h3>
              <p>Withdrawn</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center border-danger">
            <Card.Body>
              <h3 className="text-danger">{consentStats.expired}</h3>
              <p>Expired</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Action Bar */}
      <Card className="mb-3">
        <Card.Body>
          <Button
            variant="primary"
            onClick={() => {
              setSelectedConsent(null);
              resetForm();
              setShowConsentModal(true);
            }}
          >
            <FaPlus /> Record New Consent
          </Button>
        </Card.Body>
      </Card>

      <TagSnippetCard
        tenantId={userData?.tenantId || userData?.["cognito:username"]}
        regionCode={getUserRegion()}
      />

      {/* User Submitted Consents Table */}
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5>
            <FaCheck className="me-2 text-success" />
            User Submitted Consents (DPDP Act 2023)
          </h5>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={fetchSubmittedConsents}
            disabled={loadingSubmitted}
          >
            {loadingSubmitted ? "Loading..." : "Refresh"}
          </Button>
        </Card.Header>
        <Card.Body>
          {loadingSubmitted ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <Table responsive hover striped>
              <thead>
                <tr>
                  <th>Consent ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Age</th>
                  <th>Minor</th>
                  <th>Privacy</th>
                  <th>Third Party</th>
                  <th>Email Marketing</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submittedConsents.length > 0 ? (
                  submittedConsents.map((consent) => (
                    <tr key={consent.consentId}>
                      <td>
                        <small className="text-muted">
                          {consent.consentId.substring(0, 8)}...
                        </small>
                      </td>
                      <td>{consent.name}</td>
                      <td>{consent.email}</td>
                      <td>{consent.mobile}</td>
                      <td>{consent.age}</td>
                      <td>
                        {consent.isMinor ? (
                          <Badge bg="warning">
                            <FaExclamationTriangle /> Yes
                          </Badge>
                        ) : (
                          <Badge bg="secondary">No</Badge>
                        )}
                      </td>
                      <td>
                        {consent.consentPreferences?.privacyPolicy ? (
                          <FaCheck className="text-success" />
                        ) : (
                          <FaTimes className="text-danger" />
                        )}
                      </td>
                      <td>
                        {consent.consentPreferences?.thirdParty ? (
                          <FaCheck className="text-success" />
                        ) : (
                          <FaTimes className="text-danger" />
                        )}
                      </td>
                      <td>
                        {consent.consentPreferences?.emailMarketing ? (
                          <FaCheck className="text-success" />
                        ) : (
                          <FaTimes className="text-danger" />
                        )}
                      </td>
                      <td>
                        <Badge
                          bg={
                            consent.consentStatus === "ACTIVE"
                              ? "success"
                              : consent.consentStatus === "WITHDRAWN_ALL"
                              ? "danger"
                              : "warning"
                          }
                        >
                          {consent.consentStatus}
                        </Badge>
                      </td>
                      <td>
                        <small>{new Date(consent.createdAt).toLocaleDateString()}</small>
                      </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => viewConsentDetails(consent.email)}
                            title="View full details"
                          >
                            <FaCheck />
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEditSubmittedConsent(consent)}
                            title="Edit consent"
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteSubmittedConsent(consent.consentId)}
                            title="Delete consent"
                            disabled={consent.consentStatus === "WITHDRAWN_ALL"}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12" className="text-center py-4">
                      <div className="text-muted">
                        <FaExclamationTriangle className="me-2" />
                        No submitted consents found
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Consent Records Table */}
      <Card>
        <Card.Header>
          <h5>Consent Records</h5>
        </Card.Header>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Consent ID</th>
                <th>Purpose</th>
                <th>Data Category</th>
                <th>Processing Type</th>
                <th>Consent Method</th>
                <th>Data Principal</th>
                <th>Child Data</th>
                <th>Granted Date</th>
                <th>Validity</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {consents.length > 0 ? (
                consents.map((consent) => (
                  <tr key={consent.consentId}>
                    <td>{consent.consentId}</td>
                    <td>{consent.purpose}</td>
                    <td>{consent.dataCategory}</td>
                    <td>{consent.processingType}</td>
                    <td>{consent.consentMethod}</td>
                    <td>{consent.dataPrincipalInfo}</td>
                    <td>
                      {consent.isChildData ? (
                        <Badge bg="warning">
                          <FaExclamationTriangle /> Yes
                        </Badge>
                      ) : (
                        <Badge bg="secondary">No</Badge>
                      )}
                    </td>
                    <td>{new Date(consent.grantedDate).toLocaleDateString()}</td>
                    <td>{consent.validityPeriod}</td>
                    <td>{getStatusBadge(consent.status)}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEditClick(consent)}
                          title="Edit consent"
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => handleWithdrawConsent(consent.consentId)}
                          disabled={consent.status !== "active"}
                          title="Withdraw consent"
                        >
                          <FaTimes />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteConsent(consent.consentId)}
                          title="Delete consent"
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="text-center">
                    No consent records found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Tag Consent Events Table */}
      <Card className="mt-4">
        <Card.Header>
          <h5>Tag Consent Events</h5>
        </Card.Header>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Event ID</th>
                <th>Tenant</th>
                <th>IP Address</th>
                <th>Region</th>
                <th>Status</th>
                <th>Decided At</th>
                <th>Page URL</th>
                <th>User Agent</th>
              </tr>
            </thead>
            <tbody>
              {tagEvents.length > 0 ? (
                tagEvents.map((e) => (
                  <tr key={e.consentId}>
                    <td>{e.consentId}</td>
                    <td>{e.tenantId}</td>
                    <td>{e.ipAddress || "-"}</td>
                    <td>{e.region}</td>
                    <td>{e.status}</td>
                    <td>{new Date(e.decidedAt).toLocaleString()}</td>
                    <td>{e.pageUrl}</td>
                    <td>{e.userAgent}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">
                    No tag consent events found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Record / Edit Consent Modal */}
      <Modal show={showConsentModal} onHide={() => setShowConsentModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedConsent ? "Edit Consent Record" : "Record New Consent"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSaveConsent}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Purpose of Processing</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    value={consentForm.purpose}
                    onChange={(e) => setConsentForm({ ...consentForm, purpose: e.target.value })}
                    placeholder="E.g., Marketing communications"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Data Category</Form.Label>
                  <Form.Select
                    required
                    value={consentForm.dataCategory}
                    onChange={(e) => setConsentForm({ ...consentForm, dataCategory: e.target.value })}
                  >
                    <option value="">Select...</option>
                    <option value="Personal Identifiable">Personal Identifiable</option>
                    <option value="Financial">Financial</option>
                    <option value="Health">Health</option>
                    <option value="Behavioral">Behavioral</option>
                    <option value="Communication">Communication</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Processing Type</Form.Label>
                  <Form.Select
                    required
                    value={consentForm.processingType}
                    onChange={(e) => setConsentForm({ ...consentForm, processingType: e.target.value })}
                  >
                    <option value="">Select...</option>
                    <option value="Collection">Collection</option>
                    <option value="Storage">Storage</option>
                    <option value="Sharing">Sharing</option>
                    <option value="Analysis">Analysis</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Retention Period</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    value={consentForm.retentionPeriod}
                    onChange={(e) => setConsentForm({ ...consentForm, retentionPeriod: e.target.value })}
                    placeholder="E.g., 2 years"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Consent Validity Period</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    value={consentForm.validityPeriod}
                    onChange={(e) => setConsentForm({ ...consentForm, validityPeriod: e.target.value })}
                    placeholder="E.g., 1 year"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Consent Method</Form.Label>
                  <Form.Select
                    required
                    value={consentForm.consentMethod}
                    onChange={(e) => setConsentForm({ ...consentForm, consentMethod: e.target.value })}
                  >
                    <option value="">Select...</option>
                    <option value="Web Form">Web Form</option>
                    <option value="Email">Email</option>
                    <option value="API">API</option>
                    <option value="Mobile App">Mobile App</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Data Principal Information</Form.Label>
              <Form.Control
                type="text"
                required
                value={consentForm.dataPrAPcipalinfo}
                onChange={(e) => setConsentForm({ ...consentForm, dataPrAPcipalinfo: e.target.value })}
                placeholder="Email or User ID"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Enable Granular Consent Controls"
                checked={consentForm.granularConsent}
                onChange={(e) => setConsentForm({ ...consentForm, granularConsent: e.target.checked })}
              />
            </Form.Group>

            {/* Child Data Section */}
            <Card className="mb-3 border-warning">
              <Card.Body>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label={<strong>This consent involves personal data of a child (below 18 years)</strong>}
                    checked={consentForm.isChildData}
                    onChange={(e) => {
                      setConsentForm({
                        ...consentForm,
                        isChildData: e.target.checked,
                        parentalConsent: e.target.checked
                          ? consentForm.parentalConsent
                          : { parentGuardianName: "", relationToChild: "", contactNumber: "", verificationConfirmed: false }
                      });
                    }}
                  />
                </Form.Group>

                {consentForm.isChildData && (
                  <>
                    <Alert variant="warning">
                      <FaExclamationTriangle className="me-2" />
                      <strong>DPDP Act 2023 Requirement:</strong> Verifiable parental consent is mandatory for processing personal data of children under 18 years of age.
                    </Alert>

                    <h6 className="mb-3">Parent/Guardian Details</h6>

                    <Form.Group className="mb-3">
                      <Form.Label>Parent/Guardian Name *</Form.Label>
                      <Form.Control
                        type="text"
                        required={consentForm.isChildData}
                        value={consentForm.parentalConsent.parentGuardianName}
                        onChange={(e) =>
                          setConsentForm({
                            ...consentForm,
                            parentalConsent: { ...consentForm.parentalConsent, parentGuardianName: e.target.value }
                          })
                        }
                        placeholder="Full name of parent or legal guardian"
                      />
                    </Form.Group>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Relation to Child *</Form.Label>
                          <Form.Select
                            required={consentForm.isChildData}
                            value={consentForm.parentalConsent.relationToChild}
                            onChange={(e) =>
                              setConsentForm({
                                ...consentForm,
                                parentalConsent: { ...consentForm.parentalConsent, relationToChild: e.target.value }
                              })
                            }
                          >
                            <option value="">Select relation...</option>
                            <option value="Father">Father</option>
                            <option value="Mother">Mother</option>
                            <option value="Legal Guardian">Legal Guardian</option>
                            <option value="Other">Other</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Contact Number *</Form.Label>
                          <Form.Control
                            type="tel"
                            required={consentForm.isChildData}
                            value={consentForm.parentalConsent.contactNumber}
                            onChange={(e) =>
                              setConsentForm({
                                ...consentForm,
                                parentalConsent: { ...consentForm.parentalConsent, contactNumber: e.target.value }
                              })
                            }
                            placeholder="+91 XXXXXXXXXX"
                            pattern="[+]?[0-9\s\-()]+"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        required={consentForm.isChildData}
                        checked={consentForm.parentalConsent.verificationConfirmed}
                        onChange={(e) =>
                          setConsentForm({
                            ...consentForm,
                            parentalConsent: { ...consentForm.parentalConsent, verificationConfirmed: e.target.checked }
                          })
                        }
                        label={
                          <span>
                            <strong>I confirm that verifiable parental consent has been obtained and documented</strong>
                            <br />
                            <small className="text-muted">
                              This includes appropriate technical and organizational measures to authenticate that the parent/guardian is an adult acting on behalf of the child.
                            </small>
                          </span>
                        }
                      />
                    </Form.Group>
                  </>
                )}
              </Card.Body>
            </Card>

            <Alert variant="info">
              <FaExclamationTriangle /> Consent must be specific, free, informed, unconditional, and unambiguous as per DPDP Act 2023
            </Alert>

            <Button variant="primary" type="submit">
              {selectedConsent ? "Update Consent" : "Record Consent"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Consent Details Modal */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Consent Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailedConsent && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <h6>User Information</h6>
                  <p><strong>Name:</strong> {detailedConsent.name}</p>
                  <p><strong>Email:</strong> {detailedConsent.email}</p>
                  <p><strong>Mobile:</strong> {detailedConsent.mobile}</p>
                  <p><strong>Age:</strong> {detailedConsent.age}</p>
                  <p>
                    <strong>Minor Status:</strong>{" "}
                    {detailedConsent.isMinor ? (
                      <Badge bg="warning">Minor (Guardian consent required)</Badge>
                    ) : (
                      <Badge bg="success">Adult</Badge>
                    )}
                  </p>
                </Col>
                <Col md={6}>
                  <h6>Consent Status</h6>
                  <p><strong>Status:</strong> {getStatusBadge(detailedConsent.consentStatus)}</p>
                  <p><strong>Created:</strong> {new Date(detailedConsent.createdAt).toLocaleString()}</p>
                  <p><strong>Last Updated:</strong> {new Date(detailedConsent.lastUpdatedAt).toLocaleString()}</p>
                </Col>
              </Row>

              <h6>Consent Preferences</h6>
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <td>Privacy Policy</td>
                    <td>{detailedConsent.consentPreferences?.privacyPolicy ? <FaCheck className="text-success" /> : <FaTimes className="text-danger" />}</td>
                  </tr>
                  <tr>
                    <td>Third Party Sharing</td>
                    <td>{detailedConsent.consentPreferences?.thirdParty ? <FaCheck className="text-success" /> : <FaTimes className="text-danger" />}</td>
                  </tr>
                  <tr>
                    <td>Email Marketing</td>
                    <td>{detailedConsent.consentPreferences?.emailMarketing ? <FaCheck className="text-success" /> : <FaTimes className="text-danger" />}</td>
                  </tr>
                </tbody>
              </Table>

              {detailedConsent.auditTrail && (
                <>
                  <h6 className="mt-3">Audit Trail</h6>
                  <div className="bg-light p-3 rounded" style={{ maxHeight: "200px", overflow: "auto" }}>
                    <pre style={{ margin: 0, fontSize: "12px" }}>
                      {JSON.stringify(JSON.parse(detailedConsent.auditTrail), null, 2)}
                    </pre>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Submitted Consent Modal */}
      <Modal show={showEditSubmittedModal} onHide={() => setShowEditSubmittedModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit User Consent</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSaveEditSubmittedConsent}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Full Name *</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email Address *</Form.Label>
                  <Form.Control
                    type="email"
                    required
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Mobile Number *</Form.Label>
                  <Form.Control
                    type="tel"
                    required
                    value={editForm.mobile}
                    onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                    pattern="[0-9]{10}"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Age *</Form.Label>
                  <Form.Control
                    type="number"
                    required
                    min="1"
                    max="120"
                    value={editForm.age}
                    onChange={(e) => setEditForm({ ...editForm, age: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            {parseInt(editForm.age) < 18 && (
              <Card className="mb-3 border-warning">
                <Card.Body>
                  <Alert variant="warning">
                    <FaExclamationTriangle className="me-2" />
                    <strong>Minor Detected:</strong> Guardian information is required
                  </Alert>

                  <Form.Group className="mb-3">
                    <Form.Label>Guardian Name *</Form.Label>
                    <Form.Control
                      type="text"
                      required={parseInt(editForm.age) < 18}
                      value={editForm.guardianName}
                      onChange={(e) => setEditForm({ ...editForm, guardianName: e.target.value })}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Relationship *</Form.Label>
                        <Form.Select
                          required={parseInt(editForm.age) < 18}
                          value={editForm.guardianRelationship}
                          onChange={(e) => setEditForm({ ...editForm, guardianRelationship: e.target.value })}
                        >
                          <option value="">Select...</option>
                          <option value="Father">Father</option>
                          <option value="Mother">Mother</option>
                          <option value="Legal Guardian">Legal Guardian</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Guardian Mobile *</Form.Label>
                        <Form.Control
                          type="tel"
                          required={parseInt(editForm.age) < 18}
                          value={editForm.guardianMobile}
                          onChange={(e) => setEditForm({ ...editForm, guardianMobile: e.target.value })}
                          pattern="[0-9]{10}"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            <Card className="mb-3">
              <Card.Body>
                <h6>Consent Preferences</h6>
                <Form.Group className="mb-2">
                  <Form.Check
                    type="checkbox"
                    label="Privacy Policy Consent (Mandatory)"
                    checked={editForm.privacyPolicyConsent}
                    onChange={(e) => setEditForm({ ...editForm, privacyPolicyConsent: e.target.checked })}
                    disabled
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Check
                    type="checkbox"
                    label="Third-Party Data Sharing"
                    checked={editForm.thirdPartyConsent}
                    onChange={(e) => setEditForm({ ...editForm, thirdPartyConsent: e.target.checked })}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Check
                    type="checkbox"
                    label="Email Marketing Communications"
                    checked={editForm.emailMarketingConsent}
                    onChange={(e) => setEditForm({ ...editForm, emailMarketingConsent: e.target.checked })}
                  />
                </Form.Group>
              </Card.Body>
            </Card>

            <Alert variant="info">
              <small>Editing will create a new version of this consent with an audit trail entry</small>
            </Alert>

            <div className="d-flex gap-2">
              <Button variant="primary" type="submit">
                Save Changes
              </Button>
              <Button variant="secondary" onClick={() => setShowEditSubmittedModal(false)}>
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ConsentManagementTab;